import { useState, useRef, useCallback } from "react";

// ════════════════════════════════════════════════════════════════
// Multi-Marketplace Import — Amazon, Flipkart, Meesho per-order P&L
// ════════════════════════════════════════════════════════════════

const INR = v => "₹" + (Math.round(Number(v)||0)).toLocaleString("en-IN");
const R2  = v => Math.round((Number(v)||0)*100)/100;
const PCT = v => (Math.round((Number(v)||0)*10)/10) + "%";
const GST_RATE = 18;

const PLATFORM_META = {
  amazon:   { name:"Amazon",   color:"#c45000" },
  flipkart: { name:"Flipkart", color:"#1b57c0" },
  meesho:   { name:"Meesho",   color:"#9f2089" },
};

// ── Robust CSV parser (handles quoted commas) ──────────────────
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const splitLine = line => {
    const out = []; let cur = "", q = false;
    for (let i=0;i<line.length;i++){
      const ch=line[i];
      if (ch === '"'){ q=!q; }
      else if (ch === "," && !q){ out.push(cur); cur=""; }
      else cur+=ch;
    }
    out.push(cur);
    return out.map(s=>s.replace(/^"|"$/g,"").trim());
  };
  const headers = splitLine(lines[0]).map(h=>h.toLowerCase());
  return lines.slice(1).filter(l=>l.trim()).map(line => {
    const vals = splitLine(line);
    const o = {};
    headers.forEach((h,i)=>{ o[h]=(vals[i]||"").trim(); });
    return o;
  });
}

// ── Platform detection ─────────────────────────────────────────
function detectPlatform(rows) {
  const keys = Object.keys(rows[0]||{});
  const has = k => keys.some(key => key.includes(k));
  // Amazon MTR / settlement report
  if (has("settlement id") || has("order id") && has("product sales")) return "amazon";
  if (has("amazon order id") || has("merchant sku") && has("selling fees")) return "amazon";
  // Flipkart settlement
  if (has("order item id") && has("commission")) return "flipkart";
  if (has("order_id") && (has("settlement_value")||has("commission_rate"))) return "flipkart";
  // Meesho
  if (has("sub order no") || has("supplier listed price") || has("meesho")) return "meesho";
  if (has("final settlement amount") && has("product gst")) return "meesho";
  return "unknown";
}

// ── Mappers — normalize each platform to common schema ─────────
function mapAmazon(rows) {
  return rows.filter(r => {
    const t = (r["transaction type"]||r["type"]||"").toLowerCase();
    return !t || t.includes("order") || t.includes("shipment") || t.includes("refund");
  }).map(r => {
    const isRefund = (r["transaction type"]||r["type"]||"").toLowerCase().includes("refund");
    const sales = parseFloat(r["product sales"]||r["item price"]||r["our price"]||0);
    const fees  = Math.abs(parseFloat(r["selling fees"]||r["referral fee"]||0));
    const fba   = Math.abs(parseFloat(r["fba fees"]||r["fulfilment by amazon fees"]||r["fba fulfilment fees"]||0));
    const other = Math.abs(parseFloat(r["other transaction fees"]||r["other"]||0));
    const tcs   = Math.abs(parseFloat(r["marketplace withheld tax"]||r["tcs"]||0));
    const total = parseFloat(r["total"]||r["net"]||0);
    return {
      platform:    "amazon",
      orderId:     r["order id"]||r["amazon order id"]||"",
      sku:         r["sku"]||r["merchant sku"]||"",
      productName: r["description"]||r["product name"]||r["title"]||"",
      orderDate:   (r["date/time"]||r["date"]||r["order date"]||"").slice(0,10),
      qty:         parseInt(r["quantity"]||r["qty"]||1) || 1,
      isReturn:    isRefund,
      sellerAmt:   sales,
      commission:  fees,
      logistics:   fba,
      fixedCost:   other,
      tcs, tds: 0,
      settled:     total || (sales - fees - fba - other - tcs),
      state:       r["ship to state"]||r["state"]||"",
    };
  });
}

function mapFlipkart(rows) {
  return rows.map(r => {
    const isReturn = (r["order type"]||r["event type"]||"").toLowerCase().includes("return");
    const sp    = parseFloat(r["settlement_value"]||r["settlement value"]||r["sale amount (rs.)"]||r["sale value"]||0);
    const comm  = Math.abs(parseFloat(r["commission"]||r["commission (rs.)"]||r["marketplace fee"]||0));
    const ship  = Math.abs(parseFloat(r["shipping fee"]||r["shipping fee (rs.)"]||r["logistics fee"]||0));
    const collect= Math.abs(parseFloat(r["collection fee"]||r["collection fee (rs.)"]||0));
    const fixed = Math.abs(parseFloat(r["fixed fee"]||r["fixed fee (rs.)"]||0));
    const tcs   = Math.abs(parseFloat(r["tcs"]||r["tcs (rs.)"]||0));
    const tds   = Math.abs(parseFloat(r["tds"]||r["tds (rs.)"]||0));
    const net   = parseFloat(r["settlement_value"]||r["net settlement"]||r["bank settlement value (rs.)"]||0);
    return {
      platform:    "flipkart",
      orderId:     r["order_id"]||r["order id"]||r["order item id"]||"",
      sku:         r["sku"]||r["fsn"]||r["listing_id"]||"",
      productName: r["product"]||r["product title"]||r["title"]||"",
      orderDate:   (r["order_date"]||r["order date"]||r["dispatch date"]||"").slice(0,10),
      qty:         parseInt(r["quantity"]||r["item quantity"]||1) || 1,
      isReturn,
      sellerAmt:   sp,
      commission:  comm,
      logistics:   ship + collect,
      fixedCost:   fixed,
      tcs, tds,
      settled:     net || (sp - comm - ship - collect - fixed - tcs - tds),
      state:       r["state"]||r["customer state"]||"",
    };
  });
}

function mapMeesho(rows) {
  return rows.map(r => {
    const isReturn = (r["order status"]||r["status"]||"").toLowerCase().includes("return")
                  || (r["order status"]||r["status"]||"").toLowerCase().includes("rto");
    const sp    = parseFloat(r["supplier listed price (incl. gst)"]||r["supplier listed price"]||r["product price"]||0);
    const comm  = Math.abs(parseFloat(r["meesho commission (incl. gst)"]||r["meesho commission"]||r["commission"]||0));
    const ship  = Math.abs(parseFloat(r["shipping charge"]||r["logistics charges"]||r["shipping"]||0));
    const tcs   = Math.abs(parseFloat(r["tcs"]||0));
    const tds   = Math.abs(parseFloat(r["tds"]||0));
    const net   = parseFloat(r["final settlement amount"]||r["amount to be paid"]||r["net settlement"]||0);
    return {
      platform:    "meesho",
      orderId:     r["sub order no"]||r["sub order id"]||r["order id"]||"",
      sku:         r["supplier sku"]||r["sku"]||r["product id"]||"",
      productName: r["product name"]||r["product"]||r["name"]||"",
      orderDate:   (r["order date"]||r["order_date"]||"").slice(0,10),
      qty:         parseInt(r["quantity"]||r["qty"]||1) || 1,
      isReturn,
      sellerAmt:   sp,
      commission:  comm,
      logistics:   ship,
      fixedCost:   0,
      tcs, tds,
      settled:     net || (sp - comm - ship - tcs - tds),
      state:       r["state"]||"",
    };
  });
}

// ── Per-order P&L computation ──────────────────────────────────
function computePL(order, costs) {
  const isReturn = order.isReturn;
  const sp       = order.sellerAmt;
  const cogEntry = costs.skus[order.sku] || {};
  const cog      = isReturn ? 0 : (cogEntry.cost || 0) * (order.qty || 1);
  const pkg      = isReturn ? 0 : (costs.packaging || 0);
  const mktg     = costs.marketing || 0;
  const extra    = isReturn ? 0 : (costs.overhead || 0);

  const platformFees  = R2(order.commission + order.logistics + order.tcs + order.tds + order.fixedCost);
  const netFromPlat   = isReturn ? R2(-platformFees) : R2(sp - platformFees);

  const outputGST = isReturn ? 0 : R2(sp * GST_RATE / (100 + GST_RATE));
  const inputGST  = R2(cog * GST_RATE / 100);
  const netGST    = isReturn ? 0 : R2(outputGST - inputGST);

  const profit = R2(netFromPlat - cog - pkg - mktg - extra - netGST);
  const margin = sp > 0 ? R2(profit / sp * 100) : (profit >= 0 ? 0 : -100);

  return { ...order, cog, pkg, mktg, extra, platformFees, netFromPlat, outputGST, inputGST, netGST, profit, margin, isProfitable: profit>=0 };
}

// ════════════════════════════════════════════════════════════════
export default function PageMarketplaceImport({ toast }) {
  const [orders, setOrders] = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_mp_orders")||"[]"); } catch { return []; }
  });
  const [costs, setCosts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_mp_costs")||'{"skus":{},"packaging":19,"marketing":0,"overhead":0}'); }
    catch { return { skus:{}, packaging:19, marketing:0, overhead:0 }; }
  });
  const [tab, setTab] = useState("upload");
  const [platFilter, setPlatFilter] = useState("all");
  const azRef = useRef(null), fkRef = useRef(null), msRef = useRef(null);

  const saveOrders = (o) => { setOrders(o); localStorage.setItem("3june_mp_orders", JSON.stringify(o)); };
  const saveCosts  = (c) => { setCosts(c); localStorage.setItem("3june_mp_costs", JSON.stringify(c)); };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) { toast("Empty or unreadable file", "error"); return; }
      const platform = detectPlatform(rows);
      let mapped = [];
      if (platform === "amazon")   mapped = mapAmazon(rows);
      else if (platform === "flipkart") mapped = mapFlipkart(rows);
      else if (platform === "meesho")   mapped = mapMeesho(rows);
      else { toast("Could not detect platform — check the file format", "error"); return; }

      mapped = mapped.filter(m => m.orderId);
      const existing = JSON.parse(localStorage.getItem("3june_mp_orders")||"[]");
      const existIds = new Set(existing.map(o => o.platform + o.orderId + o.sku));
      const newOnly  = mapped.filter(m => !existIds.has(m.platform + m.orderId + m.sku));
      const merged   = [...existing, ...newOnly];
      saveOrders(merged);
      toast(`✅ ${newOnly.length} ${PLATFORM_META[platform].name} orders imported`, "success");
      setTab("orders");
    } catch(e) {
      toast("Error reading file: " + e.message, "error");
    }
  }, [toast]);

  // ── Download a sample CSV so users see the expected format ──
  const downloadSample = (platform) => {
    let headers, row1, row2;
    if (platform === "amazon") {
      headers = ["order id","sku","description","quantity","product sales","selling fees","fba fees","other","total","date/time"];
      row1 = ["408-1234567-1234567","AT_03_BLACK_36","Genuine Leather Belt - Black 36","1","1199","-181","0","-20","998","2026-04-12"];
      row2 = ["408-7654321-7654321","REV_01_BROWN_34","Reversible Belt - Brown 34","1","999","-150","0","-18","831","2026-04-13"];
    } else if (platform === "flipkart") {
      headers = ["order_id","sku","product","quantity","settlement_value","commission","shipping fee","collection fee","tcs","tds","order_date"];
      row1 = ["OD123456789","AT_03_BLACK_36","Genuine Leather Belt Black 36","1","1199","-120","-65","-10","-6","-1","2026-04-12"];
      row2 = ["OD987654321","BR_01_TAN_34","Tan Formal Belt 34","1","899","-90","-65","-10","-4","-1","2026-04-13"];
    } else {
      headers = ["sub order no","supplier sku","product name","quantity","supplier listed price","meesho commission","shipping charge","tcs","tds","final settlement amount","order date"];
      row1 = ["MEESHO12345_1","AT_03_BLACK_36","Leather Belt Black","1","799","-40","-50","-4","-1","704","2026-04-12"];
      row2 = ["MEESHO67890_1","REV_01_BROWN_34","Reversible Belt Brown","1","699","-35","-50","-3","-1","610","2026-04-13"];
    }
    const csv = [headers, row1, row2].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = `3June_sample_${platform}.csv`;
    a.click();
  };

  // ── Computed P&L ──
  const allPL = orders
    .filter(o => platFilter === "all" || o.platform === platFilter)
    .map(o => computePL(o, costs));

  const S = {
    card:  { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", marginBottom:16 },
    hdr:   { padding:"12px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
    btn:   (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, background:c, color:"#fff", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:600, fontSize:13 }),
    btnOut:{ padding:"8px 14px", borderRadius:7, background:"transparent", border:"1px solid #e5e7eb", color:"#6b7280", cursor:"pointer", fontFamily:"inherit", fontSize:12 },
    th:    { padding:"8px 10px", textAlign:"right", fontSize:9, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", color:"#9ca3af", whiteSpace:"nowrap", background:"#f9fafb", borderBottom:"1px solid #e5e7eb" },
    td:    { padding:"9px 10px", textAlign:"right", fontSize:12, borderBottom:"1px solid #f3f4f6", whiteSpace:"nowrap" },
  };

  const TABS = [
    { id:"upload",  label:"📁 Upload" },
    { id:"orders",  label:"📋 Per-Order P&L" },
    { id:"compare", label:"📊 Platform Compare" },
    { id:"costs",   label:"🏷️ Costs" },
  ];

  return (
    <div style={{ padding:"24px 28px", maxWidth:1400, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontWeight:700, fontSize:20, fontFamily:"Georgia,serif" }}>🌐 Marketplace Reports</div>
        <div style={{ fontSize:13, color:"#9ca3af", marginTop:2 }}>Upload Amazon, Flipkart or Meesho settlement reports — get per-order profit & loss</div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:20, borderBottom:"1px solid #e5e7eb", paddingBottom:0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:"9px 16px", border:"none", background:"transparent", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:600,
              color: tab===t.id ? "#F47920" : "#9ca3af",
              borderBottom: tab===t.id ? "2px solid #F47920" : "2px solid transparent", marginBottom:-1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ UPLOAD ══ */}
      {tab === "upload" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
            {[
              { id:"amazon",   ref:azRef, color:"#c45000", bg:"#fff3e8", border:"#ffd4a8", icon:"📦", hint:"MTR / Settlement Report CSV" },
              { id:"flipkart", ref:fkRef, color:"#1b57c0", bg:"#ebf2ff", border:"#b8d0ff", icon:"🛒", hint:"Settlement / Orders CSV" },
              { id:"meesho",   ref:msRef, color:"#9f2089", bg:"#fdf0fa", border:"#f0c0e8", icon:"🧣", hint:"Payment / Settlement CSV" },
            ].map(p => {
              const count = orders.filter(o=>o.platform===p.id).length;
              return (
                <div key={p.id}
                  onClick={()=>p.ref.current?.click()}
                  onDragOver={e=>e.preventDefault()}
                  onDrop={e=>{ e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                  style={{ border:`2px dashed ${p.border}`, borderRadius:10, padding:"28px 20px", textAlign:"center", cursor:"pointer", background:p.bg }}>
                  <input ref={p.ref} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0])}/>
                  <div style={{ fontSize:32, marginBottom:8 }}>{p.icon}</div>
                  <div style={{ fontWeight:700, fontSize:16, color:p.color, marginBottom:4 }}>{PLATFORM_META[p.id].name}</div>
                  <div style={{ fontSize:11, color:"#9ca3af", marginBottom:10 }}>{p.hint}</div>
                  {count > 0
                    ? <div style={{ fontSize:12, color:"#15803d", fontWeight:600 }}>✅ {count} orders loaded</div>
                    : <div style={{ fontSize:11, color:"#9ca3af" }}>Drop CSV or click</div>}
                </div>
              );
            })}
          </div>
          <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"12px 16px", fontSize:12, color:"#1e40af", lineHeight:1.6 }}>
            💡 <strong>Where to find these reports:</strong><br/>
            <strong>Amazon</strong>: Seller Central → Reports → Payments → Date Range Reports → Transaction view<br/>
            <strong>Flipkart</strong>: Seller Hub → Payments → Settlement → Download settlement report<br/>
            <strong>Meesho</strong>: Supplier Panel → Payments → Download payment file
          </div>
          <div style={{ marginTop:12, padding:"12px 16px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8 }}>
            <div style={{ fontSize:12, color:"#374151", marginBottom:8 }}>📄 <strong>Not sure of the format?</strong> Download a sample file to see what columns we expect:</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <button style={{ ...S.btnOut, fontSize:11 }} onClick={()=>downloadSample("amazon")}>⬇ Amazon sample</button>
              <button style={{ ...S.btnOut, fontSize:11 }} onClick={()=>downloadSample("flipkart")}>⬇ Flipkart sample</button>
              <button style={{ ...S.btnOut, fontSize:11 }} onClick={()=>downloadSample("meesho")}>⬇ Meesho sample</button>
            </div>
          </div>
          {orders.length > 0 && (
            <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
              <button style={S.btnOut} onClick={()=>{ if(window.confirm("Clear all imported orders?")){ saveOrders([]); toast("Cleared"); } }}>Clear all data</button>
            </div>
          )}
        </div>
      )}

      {/* ══ PER-ORDER P&L ══ */}
      {tab === "orders" && (() => {
        if (!allPL.length) return (
          <div style={{ ...S.card, padding:48, textAlign:"center", color:"#9ca3af" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>No orders yet</div>
            <div style={{ fontSize:12, marginBottom:16 }}>Upload a settlement report from the Upload tab.</div>
            <button style={S.btn()} onClick={()=>setTab("upload")}>📁 Go to Upload →</button>
          </div>
        );
        const totRev  = allPL.reduce((a,r)=>a+r.sellerAmt,0);
        const totFees = allPL.reduce((a,r)=>a+r.platformFees,0);
        const totProf = allPL.reduce((a,r)=>a+r.profit,0);
        const avgMarg = totRev>0 ? R2(totProf/totRev*100) : 0;
        const profCount = allPL.filter(r=>r.isProfitable).length;
        const platforms = [...new Set(orders.map(o=>o.platform))];

        const exportCSV = () => {
          const hdr = ["Platform","Order ID","SKU","Date","Return","Seller Amt","Commission","Logistics","TCS","TDS","Fixed","Platform Fees","COGS","Marketing","Net GST","Net from Platform","Profit","Margin %"];
          const rows = allPL.map(r=>[PLATFORM_META[r.platform].name,r.orderId,r.sku,r.orderDate,r.isReturn?"YES":"NO",r.sellerAmt,r.commission,r.logistics,r.tcs,r.tds,r.fixedCost,r.platformFees,r.cog,r.mktg,r.netGST,r.netFromPlat,r.profit,r.margin]);
          const csv = [hdr,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
          const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download="Marketplace_PnL.csv"; a.click();
          toast("Exported ✓","success");
        };

        return (
          <div>
            {/* Platform filter + export */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={()=>setPlatFilter("all")} style={{ padding:"5px 14px", borderRadius:20, fontSize:12, cursor:"pointer", border:"1px solid #e5e7eb", fontFamily:"inherit", background: platFilter==="all"?"#F47920":"transparent", color: platFilter==="all"?"#fff":"#6b7280" }}>All</button>
                {platforms.map(p => (
                  <button key={p} onClick={()=>setPlatFilter(p)} style={{ padding:"5px 14px", borderRadius:20, fontSize:12, cursor:"pointer", border:"1px solid #e5e7eb", fontFamily:"inherit", background: platFilter===p?PLATFORM_META[p].color:"transparent", color: platFilter===p?"#fff":"#6b7280" }}>{PLATFORM_META[p].name}</button>
                ))}
              </div>
              <button style={S.btn("#15803d")} onClick={exportCSV}>⬇ Export P&L</button>
            </div>

            {/* KPIs */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:16 }}>
              {[
                { label:"Orders", value:allPL.length, color:"#1A3A5C" },
                { label:"Revenue", value:INR(totRev), color:"#2563eb" },
                { label:"Platform Fees", value:INR(totFees), color:"#dc2626" },
                { label:"Net Profit", value:INR(totProf), color:totProf>=0?"#15803d":"#dc2626", sub:`${avgMarg}% margin` },
                { label:"Profitable", value:`${profCount}/${allPL.length}`, color:"#7e22ce" },
              ].map(k=>(
                <div key={k.label} style={{ background:"#fff", borderRadius:9, border:"1px solid #e5e7eb", padding:"11px 14px", borderLeft:"3px solid "+k.color }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                  <div style={{ fontSize:18, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
                  {k.sub && <div style={{ fontSize:10, color:"#9ca3af" }}>{k.sub}</div>}
                </div>
              ))}
            </div>

            {/* Table */}
            <div style={S.card}>
              <div style={S.hdr}><span>Per-Order Breakdown</span></div>
              <div style={{ overflowX:"auto", maxHeight:520, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead style={{ position:"sticky", top:0 }}>
                    <tr>
                      <th style={{ ...S.th, textAlign:"left" }}>Order / SKU</th>
                      <th style={S.th}>Platform</th>
                      <th style={S.th}>Revenue</th>
                      <th style={S.th}>Fees</th>
                      <th style={S.th}>COGS</th>
                      <th style={S.th}>Mktg</th>
                      <th style={S.th}>Net GST</th>
                      <th style={S.th}>Net(Plat)</th>
                      <th style={S.th}>Profit</th>
                      <th style={S.th}>Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPL.map((r,i) => {
                      const pm = PLATFORM_META[r.platform];
                      const bg = r.isReturn?"#fff5f5":!r.isProfitable?"#fef2f2":i%2?"#fafafa":"#fff";
                      return (
                        <tr key={r.platform+r.orderId+i} style={{ background:bg }}>
                          <td style={{ ...S.td, textAlign:"left" }}>
                            <div style={{ fontFamily:"monospace", fontSize:10, color:"#374151" }}>{r.orderId.slice(0,18)}</div>
                            <div style={{ fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{r.sku.slice(0,20)}</div>
                            {r.isReturn && <div style={{ fontSize:9, color:"#dc2626" }}>↩ Return</div>}
                          </td>
                          <td style={S.td}><span style={{ fontSize:10, padding:"1px 7px", borderRadius:8, background:pm.color+"22", color:pm.color, fontWeight:600 }}>{pm.name}</span></td>
                          <td style={{ ...S.td, fontWeight:500 }}>{INR(r.sellerAmt)}</td>
                          <td style={{ ...S.td, color:"#dc2626" }}>−{INR(r.platformFees)}</td>
                          <td style={{ ...S.td, color:r.isReturn?"#9ca3af":"#d97706" }}>{r.isReturn?"— ↩":r.cog>0?INR(r.cog):"—"}</td>
                          <td style={{ ...S.td, color:"#b5003c" }}>{INR(r.mktg)}</td>
                          <td style={{ ...S.td, color:r.isReturn?"#9ca3af":"#6366f1" }}>{r.isReturn?"— ↩":INR(r.netGST)}</td>
                          <td style={{ ...S.td, fontWeight:500, color:r.netFromPlat>=0?"#15803d":"#dc2626" }}>{INR(r.netFromPlat)}</td>
                          <td style={{ ...S.td, fontWeight:700, fontSize:13, color:r.isProfitable?"#15803d":"#dc2626" }}>{r.isProfitable?"▲":"▼"}{INR(Math.abs(r.profit))}</td>
                          <td style={{ ...S.td, color:r.margin>=20?"#15803d":r.margin>=0?"#d97706":"#dc2626" }}>{PCT(r.margin)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background:"#f9fafb", fontWeight:700, position:"sticky", bottom:0 }}>
                      <td colSpan={2} style={{ ...S.td, textAlign:"left", fontWeight:700 }}>TOTAL ({allPL.length})</td>
                      <td style={{ ...S.td, fontWeight:700 }}>{INR(totRev)}</td>
                      <td style={{ ...S.td, fontWeight:700, color:"#dc2626" }}>−{INR(totFees)}</td>
                      <td style={{ ...S.td, fontWeight:700, color:"#d97706" }}>{INR(allPL.reduce((a,r)=>a+r.cog,0))}</td>
                      <td style={{ ...S.td, fontWeight:700, color:"#b5003c" }}>{INR(allPL.reduce((a,r)=>a+r.mktg,0))}</td>
                      <td style={{ ...S.td, fontWeight:700, color:"#6366f1" }}>{INR(allPL.reduce((a,r)=>a+r.netGST,0))}</td>
                      <td style={{ ...S.td, fontWeight:700 }}>{INR(allPL.reduce((a,r)=>a+r.netFromPlat,0))}</td>
                      <td style={{ ...S.td, fontWeight:700, fontSize:14, color:totProf>=0?"#15803d":"#dc2626" }}>{totProf>=0?"▲":"▼"}{INR(Math.abs(totProf))}</td>
                      <td style={{ ...S.td, fontWeight:700, color:avgMarg>=0?"#15803d":"#dc2626" }}>{PCT(avgMarg)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ PLATFORM COMPARE ══ */}
      {tab === "compare" && (() => {
        const platforms = [...new Set(orders.map(o=>o.platform))];
        if (!platforms.length) return <div style={{ ...S.card, padding:40, textAlign:"center", color:"#9ca3af" }}>Upload reports from 2+ platforms to compare.</div>;
        const byPlat = platforms.map(p => {
          const rows = orders.filter(o=>o.platform===p).map(o=>computePL(o,costs));
          const del  = rows.filter(r=>!r.isReturn);
          const rev  = del.reduce((a,r)=>a+r.sellerAmt,0);
          const fees = del.reduce((a,r)=>a+r.platformFees,0);
          const profit = rows.reduce((a,r)=>a+r.profit,0);
          return { platform:p, orders:rows.length, returns:rows.filter(r=>r.isReturn).length, rev, fees, feePct: rev>0?R2(fees/rev*100):0, profit, margin: rev>0?R2(profit/rev*100):0 };
        }).sort((a,b)=>b.margin-a.margin);
        const best = byPlat[0];
        return (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${byPlat.length},1fr)`, gap:12, marginBottom:16 }}>
              {byPlat.map(p => {
                const pm = PLATFORM_META[p.platform];
                return (
                  <div key={p.platform} style={{ ...S.card, marginBottom:0, padding:18, borderTop:`3px solid ${pm.color}`, ...(p===best?{boxShadow:"0 4px 16px rgba(21,128,61,0.15)"}:{}) }}>
                    <div style={{ fontWeight:700, fontSize:15, color:pm.color, marginBottom:6 }}>{pm.name} {p===best?"⭐":""}</div>
                    <div style={{ fontSize:24, fontWeight:700, fontFamily:"monospace", color:p.profit>=0?"#15803d":"#dc2626", marginBottom:2 }}>{p.profit>=0?"▲":"▼"}{INR(Math.abs(p.profit))}</div>
                    <div style={{ fontSize:12, color:p.margin>=20?"#15803d":p.margin>=0?"#d97706":"#dc2626", marginBottom:12 }}>{PCT(p.margin)} margin</div>
                    {[["Orders",p.orders],["Returns",p.returns],["Revenue",INR(p.rev)],["Fees",`${INR(p.fees)} (${PCT(p.feePct)})`]].map(([l,v])=>(
                      <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"3px 0", borderBottom:"1px solid #f3f4f6" }}>
                        <span style={{ color:"#9ca3af" }}>{l}</span><span style={{ fontWeight:600 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{ background:"#eff6ff", borderRadius:8, padding:"12px 16px", fontSize:13, color:"#1e40af" }}>
              💡 <strong>{PLATFORM_META[best.platform].name}</strong> is your most profitable platform at {PCT(best.margin)} margin. Focus your inventory and ad spend here.
            </div>
          </div>
        );
      })()}

      {/* ══ COSTS ══ */}
      {tab === "costs" && (
        <div>
          <div style={S.card}>
            <div style={S.hdr}><span>Per-order costs</span><span style={{ fontSize:11, color:"#9ca3af" }}>Applied to all platforms</span></div>
            <div style={{ padding:18, display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
              {[
                ["packaging","Packaging ₹/order","19"],
                ["marketing","Marketing ₹/order","50"],
                ["overhead","Other ₹/order","0"],
              ].map(([k,label,ph]) => (
                <div key={k}>
                  <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"1px", marginBottom:5 }}>{label}</div>
                  <input type="number" placeholder={ph} value={costs[k]||""}
                    onChange={e=>saveCosts({ ...costs, [k]:parseFloat(e.target.value)||0 })}
                    style={{ width:"100%", padding:"8px 12px", border:"1px solid #d1d5db", borderRadius:6, fontSize:13, fontFamily:"inherit" }}/>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.hdr}>
              <span>COGS per SKU</span>
              <button style={S.btnOut} onClick={()=>{
                const skus = [...new Set(orders.map(o=>o.sku).filter(Boolean))];
                const updated = { ...costs.skus };
                skus.forEach(sku => { if(!updated[sku]) updated[sku] = { name: orders.find(o=>o.sku===sku)?.productName||"", cost:0 }; });
                saveCosts({ ...costs, skus:updated });
                toast(`${skus.length} SKUs detected`, "success");
              }}>Auto-detect from uploads</button>
            </div>
            <div style={{ padding:18 }}>
              {Object.keys(costs.skus||{}).length === 0 ? (
                <div style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:20 }}>
                  No SKUs yet. Upload reports, then click "Auto-detect from uploads".
                </div>
              ) : Object.entries(costs.skus).map(([sku,v]) => (
                <div key={sku} style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
                  <div style={{ flex:1, fontFamily:"monospace", fontSize:12, color:"#F47920" }}>{sku}</div>
                  <div style={{ flex:1, fontSize:12, color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{v.name}</div>
                  <input type="number" placeholder="₹ cost" value={v.cost||""}
                    onChange={e=>saveCosts({ ...costs, skus:{ ...costs.skus, [sku]:{ ...v, cost:parseFloat(e.target.value)||0 } } })}
                    style={{ width:100, padding:"6px 10px", border:"1px solid #d1d5db", borderRadius:6, fontSize:12, fontFamily:"inherit" }}/>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
